import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PropertyMapPanel from "@/components/PropertyMapPanel";
import { resolveMeasurementProvider, PROVIDER_GOOGLE } from "@/lib/measurementProvider";
import MeasurementWorkspace from "@/pages/MeasurementWorkspace";

const EMPTY = { calculator_key: "sqft_area", label: "", manual_value: "", depth_in: 3, density: 100 };

export default function NewMeasurementDialog({ open, onClose, propertyId, propertyAddress, propertyLat, propertyLng, calculators, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [mapMeasurement, setMapMeasurement] = useState({ areaSqFt: 0, lengthFt: 0 });
  const [useGoogle, setUseGoogle] = useState(false);
  useEffect(() => { if (open) { setForm(EMPTY); setMapMeasurement({ areaSqFt: 0, lengthFt: 0 }); } }, [open]);
  useEffect(() => {
    if (!open) return;
    let m = true;
    resolveMeasurementProvider({ force: true }).then((d) => { if (m) setUseGoogle(d.provider === PROVIDER_GOOGLE); });
    return () => { m = false; };
  }, [open]);
  const calc = calculators.find((c) => c.key === form.calculator_key);
  const needsDepth = calc && (calc.output_unit === "cuyd" || calc.output_unit === "ton");
  const needsDensity = calc && calc.output_unit === "ton";
  const inputUnit = calc?.input_unit || "each";

  const submit = async (e) => {
    e.preventDefault();
    const params = {};
    if (needsDepth) params.depth_in = Number(form.depth_in);
    if (needsDensity) params.density_lbs_cuft = Number(form.density);
    try {
      const { data } = await api.post("/estimator/measurements", {
        property_id: propertyId,
        calculator_key: form.calculator_key,
        feature_type: "manual",
        label: form.label || calc?.label || "Measurement",
        manual_value: Number(form.manual_value) || 0,
        calculator_params: params,
      });
      toast.success("Measurement saved");
      onCreated(data);
      onClose();
    } catch { toast.error("Failed"); }
  };

  // When Google Maps is enabled for this tenant, delegate to the Google
  // MeasurementWorkspace. Otherwise fall back to the Mapbox PropertyMapPanel.
  if (open && useGoogle) {
    return (
      <MeasurementWorkspace
        key={`${propertyId || "none"}::${propertyAddress || ""}`}
        open={open}
        onClose={onClose}
        propertyAddress={propertyAddress}
        propertyLat={propertyLat}
        propertyLng={propertyLng}
        onSaved={(m) => {
          onCreated?.(m);
          onClose?.();
        }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="new-measurement-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Add measurement</DialogTitle>
          <DialogDescription className="text-xs">
            Draw on the satellite map to auto-fill the value, or type a manual measurement below.
          </DialogDescription>
        </DialogHeader>

        {/* Phase 3 Mapbox — draw on satellite to pre-fill the value */}
        <div className="mb-4">
          <PropertyMapPanel
            initialAddress={propertyAddress}
            onChange={(m) => {
              setMapMeasurement({ areaSqFt: m.areaSqFt, lengthFt: m.lengthFt });
              // Auto-populate the manual value based on the current calculator's expected unit.
              const unit = calc?.input_unit;
              const val = unit === "linear_ft" ? m.lengthFt : m.areaSqFt;
              if (val > 0) setForm(prev => ({ ...prev, manual_value: String(val) }));
            }}
          />
          {(mapMeasurement.areaSqFt > 0 || mapMeasurement.lengthFt > 0) && (
            <div className="mt-2 text-[11px] text-zinc-500" data-testid="measurement-map-hint">
              Drawing measurements will pre-fill the value below. You can still override it manually.
            </div>
          )}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Calculator</Label>
            <Select value={form.calculator_key} onValueChange={(v) => setForm({ ...form, calculator_key: v })}>
              <SelectTrigger data-testid="measurement-form-calc"><SelectValue /></SelectTrigger>
              <SelectContent>
                {calculators.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {calc?.description && <div className="text-xs text-zinc-500 mt-1">{calc.description}</div>}
          </div>
          <div>
            <Label>Label</Label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Front lawn" data-testid="measurement-form-label" />
          </div>
          <div>
            <Label>Value ({inputUnit})</Label>
            <Input required type="number" step="0.01" value={form.manual_value}
              onChange={(e) => setForm({ ...form, manual_value: e.target.value })}
              data-testid="measurement-form-value" />
          </div>
          {needsDepth && (
            <div>
              <Label>Depth (in)</Label>
              <Input type="number" step="0.1" value={form.depth_in}
                onChange={(e) => setForm({ ...form, depth_in: e.target.value })}
                data-testid="measurement-form-depth" />
            </div>
          )}
          {needsDensity && (
            <div>
              <Label>Density (lbs/cuft)</Label>
              <Input type="number" step="1" value={form.density}
                onChange={(e) => setForm({ ...form, density: e.target.value })} />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="measurement-form-submit">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
